// Libraries
import React, { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Button } from '@cb/apricot-react';

//Constants
import { APP_URLS } from 'src/constants';
import { ERRORS_EMPTY } from 'src/types/validation';
import { useAuth } from 'src/contexts';

// Interfaces & Typing
import { ISummary } from 'src/types';


// Components
import { ErrorNotice, ContainerInline, HeaderInline, Loader } from 'src/generic/component';

// Processing
import { IData } from 'src/types/org/common/dataSchema';
import { IDIRequest } from 'src/types/org/di/diRequestSchema';
import { SupportDocFields } from './SupportDoc';
import { DiUpdateForm } from './DiUpdateForm';

interface IDIRequestProps {
  summary: ISummary;
  request: Partial<IDIRequest<IData>>;
  readOnly: boolean;
  source: string;
  searchParams: object;
}

export const DIUpdateRequestForm: FC<IDIRequestProps> = ({ summary, request: initial, readOnly, source, searchParams }) => {
  const navigate = useNavigate();
  const { userName } = useAuth();
  const [step, setStep] = useState('di');

  const [categoryId, setCategoryId] = useState('');
  const [data, setData] = useState<Partial<IDIRequest<IData>> | null>(initial);

  useEffect(() => {
    setData(initial || {});
    // If form is still able to be editted, we want to force a sign-off one every new submission
  }, [initial]);

  const [errorMessage, setErrorMessage] = useState<string | React.ReactNode>('');
  const [errorMessageOpen, setErrorMessageOpen] = useState<boolean>(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    setErrorMessageOpen(false);
    setErrorMessage(false);
  }, [step]);

  const scrollToError = () => {
    const element = document.querySelector<HTMLElement>('.cb-validation-error');
    if (element) {
      const top = element.getBoundingClientRect().top;
      window.scrollBy({ top: top - 100 });
    }
  };

  let form = <Loader />;
  if (step === 'di') {
    form = (
      <>
        <DiUpdateForm
          readOnly={readOnly}
          onComplete={(update) => {
            setCategoryId(update.categoryId || '');
            setStep('submit');
            setData(update);
          }}
          request={data || {}}
          source={source}
          searchParams={searchParams}
        />
      </>
    );
  }

  if (step === 'submit') {
    form = (
      <div className="display-flex flex-column cb-padding-top-8">
        <div>
          Thank you for submitting your request. Please allow 7 to 10 business days for processing.
          Please upload any additional information that maybe needed to process your request here or via the "Add document" link on the home page.
          <br />
        </div>
        <SupportDocFields
          app={{ ...data?.data?.appForm }}
          categoryId={categoryId}
          userName={userName || ''}
          errors={ERRORS_EMPTY}
          onUpdate={app => {
            const final = {
              ...data,
              data: {
                ...data?.data,
                appForm: {
                  ...data?.data?.appForm,
                  supportFiles: app.supportFiles,
                },
              }
            };
            setData(final);
          }}
        />
        <Button
          style={{ width: '100%' }}
          onClick={() => navigate(APP_URLS.DIHOME)}
          disabled={false}>{'Close'}</Button>
      </div>
    );
  }

  return (
    <div>
      {/* @ts-ignore  */}
      <Modal
        withHeader={false}
        withFooter={true}
        open={errorMessageOpen}
        onClose={() => {
          setErrorMessageOpen(false);
          scrollToError();
        }}>
        <ContainerInline>
          <HeaderInline style={{ marginBottom: '6px' }}>
            <i
              style={{ marginRight: '10px' }}
              className="cb-icon cb-icon-circular cb-exclamation cb-red1-color"
              aria-hidden="true"></i>
            <h4 className="cb-notification-title">
              <strong>Errors Occurred</strong>
            </h4>
          </HeaderInline>
          <div style={{ marginLeft: '42px' }} className="cb-notification-content">
            {errorMessage}
          </div>
        </ContainerInline>
      </Modal>
 
      {errorMessage && (
        <div style={{ marginBottom: '20px', marginTop: '20px' }}>
          <ErrorNotice errorMessage={errorMessage} />
        </div>
      )}
      {form}
    </div>
  );
};
